import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { ConnectedFieldModel, BusinessLogicService } from '@seahorse/domain';
import { GetFieldNamePipe } from './get-field-name.pipe';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'temp-connected-fields-indicator',
  templateUrl: './connected-fields-indicator.component.html',
  styleUrls: ['./connected-fields-indicator.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgbDropdownModule,
    TranslateModule,
    GetFieldNamePipe,
  ],
})
export class ConnectedFieldsIndicatorComponent implements OnInit {
  @Input() connectedFields: ConnectedFieldModel[] = [];
  @Input() currentValue: any;

  shouldShow = false;
  private _businessLogicService = inject(BusinessLogicService);
  private _cdr = inject(ChangeDetectorRef);

  readonly componentId = `connected-fields-${Math.random()
    .toString(36)
    .substring(2, 11)}`;

  ngOnInit(): void {
    this.evaluateVisibility();
  }

  private evaluateVisibility(): void {
    if (!this.connectedFields || this.connectedFields.length === 0) {
      this.shouldShow = false;
      return;
    }

    const needsEvaluation: ConnectedFieldModel[] = [];

    for (const cf of this.connectedFields) {
      if (!cf.businessLogic?.conditions?.length) {
        this.shouldShow = true;
        continue;
      }

      const hasTargetField = cf.businessLogic.conditions.some((c: any) =>
        c.targetField?.trim()
      );
      if (hasTargetField) {
        this.shouldShow = true;
        continue;
      }

      const hasSourceField = cf.businessLogic.conditions.some((c: any) =>
        c.sourceField?.trim()
      );
      if (hasSourceField) {
        needsEvaluation.push(cf);
      }
    }

    if (this.shouldShow || needsEvaluation.length === 0) {
      if (!this.shouldShow) {
        this.markAllUnselected();
      }
      return;
    }

    const evaluations = needsEvaluation.map((cf) =>
      this._businessLogicService
        .evaluateConditions(cf.businessLogic, this.currentValue)
        .pipe(
          map((result) => ({
            cf,
            passes: result.hasResult && result.result === true,
          }))
        )
    );

    forkJoin(evaluations).subscribe((results) => {
      results.forEach(({ cf, passes }) => {
        if (!passes) {
          cf.selected = false;
        }
      });

      this.shouldShow = results.some((r) => r.passes);

      if (!this.shouldShow) {
        this.markAllUnselected();
      }
      this._cdr.markForCheck();
    });
  }

  private markAllUnselected(): void {
    this.connectedFields.forEach((cf) => (cf.selected = false));
  }
}
